import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

type DragHandler = (dragIndex: number, hoverIndex: number) => void;

interface DraggableItemParams {
  dragItem?: DragHandler;
  dropItem?: DragHandler;
  dragItemType?: string;
  index: number;
  disabled?: boolean;
}

export default function useDraggableItem<T extends { index: number }>({
  dragItem,
  dropItem,
  dragItemType = "draggable-item",
  index,
  disabled = false,
}: DraggableItemParams) {
  const dragRef = useRef(null);
  const dropRef = useRef(null);

  const [{ isOver }, drop] = useDrop({
    accept: dragItemType,
    canDrop: () => !disabled,
    hover(item: T) {
      if (!dropRef.current || disabled) {
        return;
      }

      const dragIndex = item.index;

      if (dragIndex === index) {
        return;
      }

      dragItem?.(dragIndex, index);
      item.index = index;
    },
    drop(item: T) {
      if (!dropRef.current || disabled) {
        return;
      }

      dropItem?.(item.index, index);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: dragItemType,
    item: { index },
    canDrag: () => !disabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  preview(drop(dropRef));
  drag(dragRef);

  return { dragRef, dropRef, isDragging, isOver };
}
